import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ClickAwayListener, Paper, Popper, Typography, useMediaQuery, Badge, Card, Button } from '@mui/material';

// project import
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import CloseNotificationConfirm from 'components/dialogs/CloseNotificationConfirm';
import ConfirmDialog, { DialogAction } from 'components/dialogs/ConfirmDialog';

// assets
import { CheckCircleFilled, LikeFilled } from '@ant-design/icons';
import LeakIcon from 'assets/images/icons/leak-icon.svg';
import CheckIcon from 'assets/images/icons/check-icon.svg';

// utils && hooks
import { formatDate } from 'utils/helper';
import useUserRole from 'hooks/useUserRole';
import useEnums from 'hooks/useEnum';
import useSnackbar from 'hooks/useSnackbar';

// redux
import { useGetCompanyFormattedOpenLeaksQuery } from 'store/reducers/api/company';
import { useUpdateNotificationStatusMutation } from 'store/reducers/api/notification';
import { useGetPropertyFormattedOpenLeaksQuery } from 'store/reducers/api/property';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const primaryTextColor = '#004A6F';
const errorTextColor = '#FF4D4F';
const lightBlueColor = '#1890FF'

const LeaksNotification = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const snackbar = useSnackbar();

  const { NotificationStatusesEnum } = useEnums();
  const { activeRole } = useUserRole();

  const companyId = activeRole?.company_id;
  const propertyId = activeRole?.property_id;

  const companyQuery = useGetCompanyFormattedOpenLeaksQuery({ id: companyId! }, { skip: !companyId || !!propertyId });
  const propertyQuery = useGetPropertyFormattedOpenLeaksQuery({ id: propertyId! }, { skip: !propertyId });
  const [updateNotificationStatus] = useUpdateNotificationStatusMutation();

  const data = companyQuery.data || propertyQuery.data;

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const [openCloseAlertDialog, setOpenCloseAlertDialog] = useState<boolean>(false);
  const [openAcknowledgeAlertDialog, setOpenAcknowledgeAlertDialog] = useState<boolean>(false);

  /** holding notif id to close*/
  const closingId = useRef<string>();

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (openCloseAlertDialog || openAcknowledgeAlertDialog) {
      return;
    }

    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleCloseAlert = async (statusId: number, note?: string) => {
    setOpenCloseAlertDialog(false);
    setOpenAcknowledgeAlertDialog(false);
    try {
      if (closingId.current) {
        await updateNotificationStatus({ id: closingId.current, statusId, note }).unwrap();
        snackbar.showSuccess('Status updated');
      }
    } catch (error) {
      console.error(error);
      snackbar.showError('An error occurred');
    } finally {
      closingId.current = undefined;
    }

    // keep the list open
    setTimeout(() => {
      setOpen(true);
    }, 0);
  };

  const handleCloseConfirmDialog = () => {
    setOpenCloseAlertDialog(false);
    setOpenAcknowledgeAlertDialog(false);
    closingId.current = undefined;

    // keep the list open
    setTimeout(() => {
      setOpen(true);
    }, 0);
  };

  const actions: Record<DialogAction, () => void> = {
    ok: () => handleCloseAlert(NotificationStatusesEnum.ACKNOWLEDGED),
    cancel: handleCloseConfirmDialog
  };

  const handleDialogAction = (value: DialogAction) => {
    actions[value]?.();
  };

  return (
    <>
      <CloseNotificationConfirm
        open={openCloseAlertDialog}
        onClose={handleCloseConfirmDialog}
        onOK={(value) => handleCloseAlert(NotificationStatusesEnum.CLOSED, value)}
      />

      <ConfirmDialog
        open={openAcknowledgeAlertDialog}
        onClose={handleCloseConfirmDialog}
        onAction={handleDialogAction}
        title="Acknowledging Alert"
        text={'Acknowledging this alert will prevent future reminders'}
      />

      <Box sx={{ flexShrink: 0, ml: 2 }}>
        <IconButton
          color="secondary"
          variant="light"
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          size={'medium'}
        >
          <Badge
            badgeContent={data?.length || 0}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: lightBlueColor,
                color: '#ffffff'
              }
            }}
          >
            <img src={LeakIcon} alt="Leaks Notification" width={20} />
          </Badge>
        </IconButton>
        <Popper
          placement={matchesXs ? 'bottom' : 'bottom-end'}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [matchesXs ? -5 : 0, 9]
                }
              }
            ]
          }}
          style={{ maxWidth: 400, width: '100%' }}
        >
          {({ TransitionProps }) => (
            <Transitions type="fade" in={open} {...TransitionProps}>
              <Paper
                sx={{
                  boxShadow: theme.customShadows.z1,
                  backgroundColor: '#fafafb',
                  border: matchesXs ? '' : `1px solid ${errorTextColor}`,
                  borderRadius: '8px'
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    {!!data?.length ? (
                      <Box sx={{ paddingTop: 4, paddingBottom: 1 }}>
                        <Box display={'flex'} justifyContent={'space-between'} mb={2} paddingX={4}>
                          <Typography variant="h5" color={primaryTextColor}>
                            {data.length} {data.length > 1 ? 'Leaks' : 'Leak'}
                          </Typography>
                          <Typography variant="h6" color={primaryTextColor}>
                            {formatDate(new Date(), 'MMMM D, YYYY')}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            minHeight: '50vh',
                            maxHeight: '70vh',
                            overflowY: 'auto'
                          }}
                          paddingX={4}
                        >
                          {data.map((notif, index) => (
                            <Box mt={1} mb={2} key={index}>
                              <Card
                                elevation={0}
                                sx={{
                                  background: '#FFF',
                                  boxShadow: '4px 4px 15.3px 1px rgba(0, 0, 0, 0.10)',
                                  border: '1px solid #979797'
                                }}
                              >
                                <Box display={'flex'} columnGap={2} padding={2} justifyContent={'space-between'}>
                                  <Box display={'flex'} flexDirection={'column'}>
                                    <Typography fontSize={14} color={errorTextColor}>
                                      {notif.address}
                                    </Typography>
                                    <Typography fontSize={12} color={primaryTextColor}>
                                      {notif.room}
                                      {', '}
                                      <a href={`${notif.link}`} target="_blank" rel="noopener noreferrer">
                                        {notif.appliance}
                                      </a>
                                      {notif.rate && `, $${notif.rate}/month`}
                                    </Typography>
                                  </Box>
                                  <Box display={'flex'} flexDirection={'column'} rowGap={1}>
                                    <Button
                                      variant="contained"
                                      size="extraSmall"
                                      endIcon={<CheckCircleFilled />}
                                      sx={{ backgroundColor: primaryTextColor, whiteSpace: 'nowrap' }}
                                      onClick={() => {
                                        setOpenCloseAlertDialog(true);
                                        closingId.current = notif.id;
                                      }}
                                    >
                                      Fixed it
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="extraSmall"
                                      endIcon={<LikeFilled />}
                                      sx={{ borderColor: primaryTextColor, color: primaryTextColor, whiteSpace: 'nowrap' }}
                                      onClick={() => {
                                        setOpenAcknowledgeAlertDialog(true);
                                        closingId.current = notif.id;
                                      }}
                                    >
                                      On it
                                    </Button>
                                  </Box>
                                </Box>
                              </Card>
                              <Typography mt={0.7} fontSize={10} color={primaryTextColor}>
                                First Detected: {notif.firstDetected}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          minHeight: '50vh'
                        }}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        flexDirection={'column'}
                      >
                        <img src={CheckIcon} alt="No Notifications" width={40} />
                        <Typography mt={1} fontSize={18} fontWeight={'bold'} maxWidth={130} textAlign={'center'} color={primaryTextColor}>
                          You have no active leaks
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      </Box>
    </>
  );
};

export default LeaksNotification;
